import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  useAppKit,
  useAppKitAccount,
  useAppKitNetwork,
} from "@reown/appkit/react";
import { mainnet, bsc } from "@reown/appkit/networks";
import useContract from "../../hooks/useContract";

const paymentOptions = [
  { key: "ETH", label: "Pay With ETH", icon: "/eth.svg" },
  { key: "BNB", label: "Pay With BNB", icon: "/bnb.svg" },
  { key: "USDT", label: "Pay With USDT", icon: "/usdt.svg" },
  { key: "USDC", label: "Pay With USDC", icon: "/usdc.svg" },
];

export default function PresaleLive() {
  const [selected, setSelected] = useState("ETH");
  const [amount, setAmount] = useState("");
  const [receiveable, setReceiveable] = useState("");
  const [price, setPrice] = useState(0);
  const [ethPrice, setEthPrice] = useState(0);
  const [bscPrice, setBscPrice] = useState(0);
  const [maxBalance, setMaxBalance] = useState(null);
  const [loading, setLoading] = useState(false);

  const { open } = useAppKit();
  const { isConnected, address } = useAppKitAccount();
  const { chainId, switchNetwork } = useAppKitNetwork();

  const [stats, setStats] = useState({
    tokenSold: 0,
    usdRaised: 0,
    ethSold: 0,
    bscSold: 0,
    ethRaised: 0,
    bscRaised: 0,
  });




  const { buy, getPrice, getAppkitBalance, maxBalances, getPresaleStats } = useContract();

  const progress = 20;
  const tokenGoalValue = 10500000000000;
  const usdGoalValue = 250000;

  const tokenProgress = Math.min(
    ((stats.tokenSold || 0) / tokenGoalValue) * 100,
    100
  );

  const usdRaised = price > 0 ? Number(stats.tokenSold || 0) / Number(price) : 0;

  const usdProgress = Math.min((usdRaised / usdGoalValue) * 100, 100);




  const selectedOption =
    paymentOptions.find((item) => item.key === selected) || paymentOptions[0];



useEffect(() => {
  let mounted = true;

  const loadPresaleData = async () => {
    try {
      const [_price, _maxBalance, _stats] = await Promise.all([
        getPrice(),
        maxBalances(),
        getPresaleStats(),
      ]);

      if (!mounted) return;

      setPrice(Number(_price || 0));
      setMaxBalance(_maxBalance);
      setStats(_stats);
    } catch (err) {
      console.error(err);
    }
  };

  const loadBinancePrices = async () => {
    try {
      const [ethRes, bnbRes] = await Promise.all([
        fetch("https://api.binance.com/api/v3/ticker/price?symbol=ETHUSDT"),
        fetch("https://api.binance.com/api/v3/ticker/price?symbol=BNBUSDT"),
      ]);

      const ethData = await ethRes.json();
      const bnbData = await bnbRes.json();

      if (!mounted) return;

      setEthPrice(Number(ethData.price || 0));
      setBscPrice(Number(bnbData.price || 0));
    } catch (err) {
      console.error(err);
    }
  };

  // Initial load
  loadPresaleData();
  loadBinancePrices();

  // Silent refresh every 30 seconds
  const interval = setInterval(() => {
    loadPresaleData();
    loadBinancePrices();
  }, 30000);

  return () => {
    mounted = false;
    clearInterval(interval);
  };
}, [address]);
  const validateChain = (type, id) => {
    if (type === "ETH" && id === mainnet.id) return true;
    if (type === "BNB" && id === bsc.id) return true;

    // USDT / USDC works on current chain:
    // Ethereum wallet = ERC20
    // BSC wallet = BEP20
    if (
      (type === "USDT" || type === "USDC") &&
      (id === mainnet.id || id === bsc.id)
    ) {
      return true;
    }

    return false;
  };

  const handlePaymentTypechange = async (newPaymentType) => {
    setSelected(newPaymentType);
    setAmount("");
    setReceiveable("");

    if (!isConnected) return;

    try {
      if (newPaymentType === "ETH" && chainId !== mainnet.id) {
        await switchNetwork(mainnet);
      } else if (newPaymentType === "BNB" && chainId !== bsc.id) {
        await switchNetwork(bsc);
      }

      // USDT / USDC no auto switch.
      // It uses current wallet network: ETH or BSC.
    } catch (error) {
      console.error("Network switch failed:", error);
      toast.error("Failed to switch network. Please change manually.");
    }
  };

  const handlePaymentChange = async (e) => {
    const precision = 15;

    const formatValue = (value) => {
      if (!Number.isFinite(value)) return "";
      if (Math.abs(value) < 1e-6) return value.toFixed(precision);
      return parseFloat(value.toFixed(precision));
    };

    const inputName = e.target.name;
    const inputValue = e.target.value;

    // allow empty, 0, 0., 0.5, decimals
    if (inputValue === "") {
      if (inputName === "amount") {
        setAmount("");
        setReceiveable("");
      } else {
        setReceiveable("");
        setAmount("");
      }
      return;
    }

    if (!/^\d*\.?\d*$/.test(inputValue)) return;

    if (inputName === "amount") {
      setAmount(inputValue);
    } else if (inputName === "receiveable") {
      setReceiveable(inputValue);
    }

    if (!price) return;

    const numericValue = parseFloat(inputValue);
    if (isNaN(numericValue)) return;

    const networks = {
      ETH: { id: mainnet.id, rate: ethPrice },
      BNB: { id: bsc.id, rate: bscPrice },
    };

    const activeNetwork = Object.values(networks).find(
      (network) => network.id === chainId
    );

    const PER_USDT_TO_NATIVE = activeNetwork ? activeNetwork.rate : ethPrice;

    if (numericValue <= 0) {
      if (inputName === "amount") setReceiveable("");
      if (inputName === "receiveable") setAmount("");
      return;
    }

    if (inputName === "amount") {
      if (selected === "ETH" || selected === "BNB") {
        const value = numericValue * price * PER_USDT_TO_NATIVE;
        setReceiveable(formatValue(value).toString());
      } else if (selected === "USDT" || selected === "USDC") {
        const value = numericValue * price;
        setReceiveable(formatValue(value).toString());
      }
    }

    if (inputName === "receiveable") {
      if (selected === "ETH" || selected === "BNB") {
        const value = numericValue / price / PER_USDT_TO_NATIVE;
        setAmount(formatValue(value).toString());
      } else if (selected === "USDT" || selected === "USDC") {
        const value = numericValue / price;
        setAmount(formatValue(value).toString());
      }
    }
  };

  const handleBuy = async () => {
    if (!isConnected) {
      open();
      return;
    }

    if (!amount || Number(amount) <= 0) {
      toast.error("Enter amount");
      return;
    }

    if (!validateChain(selected, chainId)) {
      toast.error("Please use Ethereum or BSC network");
      return;
    }

    try {
      setLoading(true);

      const balance = await getAppkitBalance(address);

      if (selected === "ETH" || selected === "BNB") {
        if (Number(amount) > Number(balance)) {
          toast.error(`Not enough ${selected} balance`);
          setLoading(false);
          return;
        }
      }

      if (selected === "USDT") {
        if (Number(amount) > Number(maxBalance?.usdt || 0)) {
          toast.error("Not enough USDT balance");
          setLoading(false);
          return;
        }
      }

      if (selected === "USDC") {
        if (Number(amount) > Number(maxBalance?.usdc || maxBalance?.busd || 0)) {
          toast.error("Not enough USDC balance");
          setLoading(false);
          return;
        }
      }

      await buy(selected, amount);

      toast.success("Buy Successful");
      window.location.reload();
    } catch (err) {
      console.error(err);
      toast.error("Error in buying");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-black px- py-10">
      <div className="mx-auto max-w-[650px] rounded-2xl border border-[#c4dc5b]/25 bg-[#070707] p-6 shadow-[0_0_20px_rgba(196,220,91,0.08)]">
        <div className="mb-6">
          <h3 className="mb-2 text-lg font-semibold text-white">
            Tokens Sold
          </h3>

          <div className="rounded-lg border border-[#c4dc5b]/50 bg-[#101010] p-4">
            <div className="flex justify-between text-sm font-semibold text-white">
              <span>
                Sold: {Number(stats.tokenSold || 0).toLocaleString()}
              </span>
              <span>Goal: {tokenGoalValue.toLocaleString()} Tokens</span>
            </div>

            <div className="mt-3 h-[8px] overflow-hidden rounded-full bg-[#232323]">
              <div
                className="h-full rounded-full bg-[#c4dc5b]"
                style={{ width: `${tokenProgress}%` }}
              />
            </div>
          </div>
        </div>

        <div className="mb-10">
          <h3 className="mb-2 text-lg font-semibold text-white">
            USD Raised
          </h3>

          <div className="rounded-lg border border-[#c4dc5b]/50 bg-[#101010] p-4">
            <div className="flex justify-between text-sm font-semibold text-white">
              <span>
                Raised: ${usdRaised.toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                })}
              </span>
              <span>Goal: ${usdGoalValue.toLocaleString()}</span>
            </div>

            <div className="mt-3 h-[8px] overflow-hidden rounded-full bg-[#232323]">
              <div
                className="h-full rounded-full bg-[#c4dc5b]"
                style={{ width: `${usdProgress}%` }}
              />
            </div>
          </div>
        </div>

        <div className="mb-8">
          <h3 className="mb-3 text-lg font-semibold text-white">
            Current Price per $ETHRA Token
          </h3>
          <div className="mb-8 rounded-lg border border-[#c4dc5b]/50 bg-[#101010] px-5 py-4">
            <div className="flex items-center justify-between">
              <span className="text-base font-semibold text-white">
                Current Price
              </span>

              <span className="text-base font-bold text-[white]">
                $0.0000000200
              </span>
            </div>
          </div>
          <div className="flex justify-between rounded-lg border border-[#c4dc5b]/50 bg-[#101010] px-5 py-4 font-semibold text-white">
            <span>Min : 1 USDT</span>
            <span>1 USD = {price || "---"}</span>
          </div>
        </div>

        <div className="mb-10 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {paymentOptions.map((item) => {
            const active = selected === item.key;

            return (
              <button
                key={item.key}
                type="button"
                onClick={() => handlePaymentTypechange(item.key)}
                className={`relative flex items-center gap-3 rounded-lg border px-3 py-3 transition-all ${active
                    ? "border-[#38ff8b] bg-[#10140b] shadow-[0_0_12px_rgba(56,255,139,0.25)]"
                    : "border-[#c4dc5b]/40 bg-[#101010] hover:border-[#c4dc5b]"
                  }`}
              >
                <img src={item.icon} alt={item.label} className="h-7 w-7 object-contain" />

                <span className="text-sm font-medium text-white">{item.label}</span>

                {active && (
                  <img
                    src="https://c.animaapp.com/mch8srslncqdro/img/check-circle.svg"
                    alt="selected"
                    className="absolute right-3 h-6 w-6"
                  />
                )}
              </button>
            );
          })}
        </div>

        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {/* Amount Input */}
          <div>
            <label className="mb-2 block font-medium text-white">
              Amount ({selected})
            </label>

            <div className="relative">
              <input
                id="buy-amount"
                type="number"
                name="amount"
                value={amount}
                onChange={handlePaymentChange}
                placeholder="Enter amount"
                min="0"
                step="any"
                className="w-full rounded-lg border border-[#c4dc5b]/50 bg-[#101010] px-4 py-4 pr-12 text-white outline-none placeholder:text-gray-500 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              />

              <img
                src={selectedOption.icon}
                alt={selected}
                className="pointer-events-none absolute right-4 top-1/2 h-6 w-6 -translate-y-1/2 object-contain"
              />
            </div>
          </div>

          {/* Receiveable Tokens */}
          <div>
            <label className="mb-2 block font-medium text-white">
              Get Amount ($ETHRA)
            </label>

            <div className="relative">
              <input
                id="receiveable"
                type="number"
                name="receiveable"
                value={receiveable}
                onChange={handlePaymentChange}
                placeholder="Tokens you get"
                min="0"
                step="any"
                className="w-full rounded-lg border border-[#c4dc5b]/50 bg-[#101010] px-4 py-4 pr-12 text-white outline-none placeholder:text-gray-500 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              />

              <img
                src="/tokenlogo.png"
                alt="ETHRA"
                className="pointer-events-none absolute right-4 top-1/2 h-6 w-6 -translate-y-1/2 object-contain"
              />
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={handleBuy}
          disabled={loading}
          className="w-full rounded-full border border-[#d9ff54] bg-[linear-gradient(180deg,#c4dc5b_0%,#95bf0c_100%)] py-4 text-2xl font-bold text-black shadow-[0_0_18px_rgba(196,220,91,0.45)] transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {!isConnected
            ? "Connect Wallet →"
            : loading
              ? "Processing..."
              : `Buy With ${selected} `}
        </button>

        {isConnected && (
          <p className="mt-4 text-center text-sm text-[#c4dc5b]">
            Connected: {address?.slice(0, 6)}...{address?.slice(-4)}
          </p>
        )}
      </div>
    </section>
  );
}